import { Controller } from "@hotwired/stimulus"
import { lock, unlock } from "../helpers/body_scroll_lock"

// Modal controller — toggles a single dialog target. Body scroll is managed
// through the shared body_scroll_lock helper so overlapping modals/sheets
// don't unlock the page when only one of them closes.
//
// Optional URL-hash persistence: pass `data-modal-url-hash-value="some-hash"`
// on the controller root and the modal will:
//   - on open    → replaceState the URL with #some-hash (no new history entry)
//   - on close   → strip #some-hash back off
//   - on connect → if the URL already carries #some-hash, auto-open
//
// This restores the modal when the user navigates back to the page
// (e.g., browser back from a same-tab affiliate redirect to Amazon, or
// bfcache restore on iOS PWA). When the value is absent, behavior is
// unchanged from the pre-existing modal usages.
export default class extends Controller {
  static targets = ["dialog"]
  static values = { urlHash: String, autoOpen: Boolean }

  connect() {
    this.isOpen = false
    if (this.autoOpenValue || this.#hashMatches()) this.open()
  }

  // If this controller's element is removed from the DOM while the modal is
  // open — a Turbo Stream replacing an ancestor out from under it, e.g. the
  // Kitchen dashboard's Tomorrow row swapping in a fresh copy of itself
  // after "Add to grocery list" — close() never runs, so the shared
  // body-scroll lock never releases and the whole page stays unscrollable
  // until a hard refresh. Stimulus calls disconnect() on removal regardless
  // of the mechanism, so release the lock here as a backstop.
  disconnect() {
    if (this.isOpen) unlock()
  }

  open() {
    this.dialogTarget.classList.remove("hidden")
    this.isOpen = true
    this.#pushHash()
    lock()
    this.#trapFocus()
  }

  // Fresh-open variant for trigger buttons. Dispatches a "modal:fresh-open"
  // event before opening so child controllers can clear stale state — e.g.,
  // the grocery-selection controller listens and resets its Bought-section
  // localStorage so the user gets a clean preview every time they tap the
  // trigger. The hash-restore path (auto-open from connect()) does NOT
  // dispatch this event, because that path's whole purpose is to preserve
  // state across an Amazon round-trip.
  openFresh() {
    this.element.dispatchEvent(new CustomEvent("modal:fresh-open", { bubbles: true }))
    this.open()
  }

  close() {
    this.dialogTarget.classList.add("hidden")
    this.isOpen = false
    this.#stripHash()
    unlock()
    this.#restoreFocus()
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  #hashMatches() {
    if (!this.hasUrlHashValue || this.urlHashValue === "") return false
    return window.location.hash === `#${this.urlHashValue}`
  }

  #pushHash() {
    if (!this.hasUrlHashValue || this.urlHashValue === "") return
    if (this.#hashMatches()) return
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#${this.urlHashValue}`)
  }

  #stripHash() {
    if (!this.#hashMatches()) return
    window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`)
  }

  #trapFocus() {
    this.previouslyFocused = document.activeElement
    const focusable = this.dialogTarget.querySelector(
      "button:not([disabled]), a, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"
    )
    focusable?.focus()
  }

  #restoreFocus() {
    if (this.previouslyFocused?.focus) {
      try { this.previouslyFocused.focus() } catch { /* element gone — no-op */ }
    }
    this.previouslyFocused = null
  }
}
